import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { MultiSelect } from '@/components/custom/multi-select';
import { Save } from 'lucide-react';
import { InboxFolder, CreateInboxFolderPayload, inboxFolderService } from '@socialflows/shared-lib';
import { useToast } from '@/hooks/use-toast';

interface FolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folder?: InboxFolder;
  onSuccess: (folder: InboxFolder) => void;
}

export function FolderDialog({ open, onOpenChange, folder, onSuccess }: FolderDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  // Form state
  const [name, setName] = useState(folder?.name || '');
  const [description, setDescription] = useState(folder?.description || '');
  const [visibility, setVisibility] = useState<'PRIVATE' | 'SHARED'>(
    folder?.visibility || 'PRIVATE'
  );

  // Filter state
  const [statuses, setStatuses] = useState<string[]>(folder?.filters?.statuses || []);
  const [priorities, setPriorities] = useState<string[]>(folder?.filters?.priorities || []);
  const [aiTags, setAiTags] = useState<string[]>(folder?.filters?.aiTags || []);
  const [networks, setNetworks] = useState<string[]>(folder?.filters?.networks || []);

  // Reset form when folder changes
  useEffect(() => {
    if (folder) {
      setName(folder.name);
      setDescription(folder.description || '');
      setVisibility(folder.visibility || 'PRIVATE');
      setStatuses(folder.filters?.statuses || []);
      setPriorities(folder.filters?.priorities || []);
      setAiTags(folder.filters?.aiTags || []);
      setNetworks(folder.filters?.networks || []);
    } else {
      setName('');
      setDescription('');
      setVisibility('PRIVATE');
      setStatuses([]);
      setPriorities([]);
      setAiTags([]);
      setNetworks([]);
    }
  }, [folder]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a folder name',
        variant: 'destructive',
      });
      return;
    }

    // Check if at least one filter is selected
    const hasFilters =
      statuses.length > 0 || priorities.length > 0 || aiTags.length > 0 || networks.length > 0;
    if (!hasFilters) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one filter',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const payload: CreateInboxFolderPayload = {
        name: name.trim(),
        description: description.trim() || undefined,
        visibility,
        filters: {
          statuses: statuses.length > 0 ? statuses : undefined,
          priorities: priorities.length > 0 ? priorities : undefined,
          aiTags: aiTags.length > 0 ? aiTags : undefined,
          networks: networks.length > 0 ? networks : undefined,
        },
      };

      const result = folder
        ? await inboxFolderService.update(folder.id, payload)
        : await inboxFolderService.create(payload);

      await queryClient.invalidateQueries({ queryKey: ['inboxFolders'] });

      toast({
        title: folder ? 'Folder updated' : 'Folder created',
        description: `Your folder has been ${folder ? 'updated' : 'created'} successfully.`,
      });

      onSuccess(result);
      onOpenChange(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to ${folder ? 'update' : 'create'} folder. Please try again.`,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const statusOptions = [
    { label: 'Open', value: 'OPEN' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Resolved', value: 'RESOLVED' },
    { label: 'Closed', value: 'CLOSED' },
  ];

  const priorityOptions = [
    { label: 'Urgent', value: 'URGENT' },
    { label: 'High', value: 'HIGH' },
    { label: 'Medium', value: 'MEDIUM' },
    { label: 'Low', value: 'LOW' },
  ];

  const aiTagOptions = [
    { label: 'VIP Customer', value: 'customer:vip' },
    { label: 'Buyer Intent', value: 'intent:buy' },
    { label: 'Complaint', value: 'intent:complaint' },
    { label: 'Question', value: 'intent:question' },
  ];

  const networkOptions = [
    { label: 'Facebook', value: 'FACEBOOK' },
    { label: 'Instagram', value: 'INSTAGRAM' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{folder ? 'Edit Folder' : 'Create Custom Folder'}</DialogTitle>
          <DialogDescription>
            Create a custom folder with specific filters to organize your inbox tickets.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Folder Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., High Priority Buyers"
                maxLength={50}
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description"
                maxLength={200}
                rows={2}
              />
            </div>

            <div>
              <Label>Visibility</Label>
              <RadioGroup
                value={visibility}
                onValueChange={(v) => setVisibility(v as 'PRIVATE' | 'SHARED')}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="PRIVATE" id="private" />
                  <Label htmlFor="private" className="font-normal">
                    Private (only visible to you)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="SHARED" id="shared" />
                  <Label htmlFor="shared" className="font-normal">
                    Shared (visible to all team members)
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          {/* Filters */}
          <div className="space-y-4 border-t pt-4">
            <h3 className="font-medium">Filter Criteria</h3>
            <p className="text-sm text-muted-foreground">
              Select one or more filters. Tickets matching the selected criteria will appear in this
              folder.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Status</Label>
                <MultiSelect
                  options={statusOptions}
                  defaultValue={statuses}
                  onValueChange={setStatuses}
                  placeholder="Select statuses"
                />
              </div>

              <div>
                <Label>Priority</Label>
                <MultiSelect
                  options={priorityOptions}
                  defaultValue={priorities}
                  onValueChange={setPriorities}
                  placeholder="Select priorities"
                />
              </div>

              <div>
                <Label>AI Tags</Label>
                <MultiSelect
                  options={aiTagOptions}
                  defaultValue={aiTags}
                  onValueChange={setAiTags}
                  placeholder="Select AI tags"
                />
              </div>

              <div>
                <Label>Network</Label>
                <MultiSelect
                  options={networkOptions}
                  defaultValue={networks}
                  onValueChange={setNetworks}
                  placeholder="Select networks"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : 'Save Folder'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
